import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Send, Loader2, QrCode, CheckCircle, IndianRupee, Upload, ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistrationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [delegatePhotoFile, setDelegatePhotoFile] = useState(null);
  const [delegatePhotoPreview, setDelegatePhotoPreview] = useState('');
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [aadhaarPreview, setAadhaarPreview] = useState('');
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    designation: '',
    institution: '',
    city: '',
    registration_type: '',
    gos_membership_number: '',
    upi_transaction_id: '',
  });

  const isGOSMember = formData.registration_type.startsWith('gos_');
  const isAppliedGOS = formData.registration_type.startsWith('applied_gos_');

  const registrationTypes = [
    { value: 'gos_regular', label: 'GOS Member - Regular', amount: 649000, display: '₹6,490 (₹5,500 + 18% GST)' },
    { value: 'gos_spot', label: 'GOS Member - Spot Registration', amount: 826000, display: '₹8,260 (₹7,000 + 18% GST)' },
    { value: 'applied_gos_regular', label: 'Applied for GOS Membership - Regular', amount: 649000, display: '₹6,490 (₹5,500 + 18% GST)' },
    { value: 'applied_gos_spot', label: 'Applied for GOS Membership - Spot Registration', amount: 826000, display: '₹8,260 (₹7,000 + 18% GST)' },
    { value: 'non_gos_regular', label: 'Non-GOS Member - Regular', amount: 767000, display: '₹7,670 (₹6,500 + 18% GST)' },
    { value: 'non_gos_spot', label: 'Non-GOS Member - Spot Registration', amount: 944000, display: '₹9,440 (₹8,000 + 18% GST)' },
    { value: 'spouse_resident_regular', label: 'Spouse/Resident - Regular', amount: 590000, display: '₹5,900 (₹5,000 + 18% GST)' },
    { value: 'spouse_resident_spot', label: 'Spouse/Resident - Spot Registration', amount: 767000, display: '₹7,670 (₹6,500 + 18% GST)' },
    { value: 'senior_70_complimentary', label: 'Senior (70+ years) - Complimentary', amount: 0, display: 'Complimentary (Aadhaar card required)' },
    { value: 'national_faculty_complimentary', label: 'National Faculty - Complimentary', amount: 0, display: 'Complimentary (National Faculty)' },
  ];

  const isSenior70 = formData.registration_type === 'senior_70_complimentary';
  const isNationalFaculty = formData.registration_type === 'national_faculty_complimentary';
  const isComplimentary = isSenior70 || isNationalFaculty;

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedType = registrationTypes.find((t) => t.value === formData.registration_type);
      if (!selectedType) {
        toast.error('Please select a registration type');
        setLoading(false);
        return;
      }

      const isSeniorType = formData.registration_type === 'senior_70_complimentary';
      const isComplimentaryType = isSeniorType || formData.registration_type === 'national_faculty_complimentary';

      if (!isComplimentaryType && !formData.upi_transaction_id.trim() && !screenshotFile) {
        toast.error('Please enter UPI Transaction ID or upload a payment screenshot');
        setLoading(false);
        return;
      }

      if (isSeniorType && !aadhaarFile) {
        toast.error('Please upload your Aadhaar card for age verification');
        setLoading(false);
        return;
      }

      let screenshotUrl = '';
      if (screenshotFile) {
        setUploadingScreenshot(true);
        const uploadForm = new FormData();
        uploadForm.append('file', screenshotFile);
        const uploadRes = await axios.post(`${API}/upload-screenshot`, uploadForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        screenshotUrl = uploadRes.data.url;
      }

      let delegatePhotoUrl = '';
      if (delegatePhotoFile) {
        const photoForm = new FormData();
        photoForm.append('file', delegatePhotoFile);
        const photoRes = await axios.post(`${API}/upload-screenshot`, photoForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        delegatePhotoUrl = photoRes.data.url;
      }
      let aadhaarUrl = '';
      if (aadhaarFile) {
        const aadhaarForm = new FormData();
        aadhaarForm.append('file', aadhaarFile);
        const aadhaarRes = await axios.post(`${API}/upload-screenshot`, aadhaarForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        aadhaarUrl = aadhaarRes.data.url;
      }
      setUploadingScreenshot(false);

      const registrationData = {
        ...formData,
        email: formData.email.trim() || undefined,
        amount: selectedType.amount,
        payment_method: isComplimentaryType ? 'complimentary' : 'upi',
        payment_screenshot_url: screenshotUrl || undefined,
        delegate_photo_url: delegatePhotoUrl || undefined,
        aadhaar_card_url: aadhaarUrl || undefined,
      };

      await axios.post(`${API}/registrations`, registrationData);
      toast.success(isComplimentaryType ? 'Registration submitted successfully! Your complimentary registration is being verified.' : 'Registration submitted successfully! We will verify your payment and confirm shortly.');
      setSubmitted(true);
      setScreenshotFile(null);
      setScreenshotPreview('');
      setDelegatePhotoFile(null);
      setDelegatePhotoPreview('');
      setAadhaarFile(null);
      setAadhaarPreview('');
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        designation: '',
        institution: '',
        city: '',
        registration_type: '',
        gos_membership_number: '',
        upi_transaction_id: '',
      });
    } catch (error) {
      console.error('Registration error:', error);
      setUploadingScreenshot(false);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-32 pb-16" data-testid="registration-page">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="glass-card p-12 text-center neon-glow" data-testid="registration-success">
            <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4 gradient-text">Registration Submitted!</h2>
            <p className="text-lg text-slate-300 mb-4">
              Thank you for registering for AGOC 2026.
            </p>
            <p className="text-slate-400 mb-8">
              Our team will verify your payment and send a confirmation to your email shortly.
              For any queries, contact us at <a href="mailto:sgaosurat2025@gmail.com" className="text-[#FF6F4A] hover:underline">sgaosurat2025@gmail.com</a>
            </p>
            <Button
              onClick={() => setSubmitted(false)}
              className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold px-8"
              data-testid="register-another-button"
            >
              Register Another Delegate
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="registration-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-4" data-testid="registration-title">
            Conference <span className="gradient-text">Registration</span>
          </h1>
          <p className="text-lg text-slate-300">
            Secure your spot at AGOC 2026
          </p>
        </div>

        {/* Fee Structure Table */}
        <div className="glass-card p-8 md:p-12 mb-8">
          <h2 className="text-2xl font-semibold mb-4">Registration Fee Structure</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="fee-table">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="py-4 px-4 text-slate-300 font-semibold">Category</th>
                  <th className="py-4 px-4 text-center text-[#FFB6C1] font-semibold">Regular</th>
                  <th className="py-4 px-4 text-center text-white font-semibold">Spot<br/><span className="text-xs font-normal">(At Venue)</span></th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                  <td className="py-4 px-4 font-medium text-white">GOS Members</td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-[#FFB6C1] font-bold text-lg">₹6,490</span>
                    <br/><span className="text-slate-500 text-xs">(₹5,500 + 18% GST)</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-white font-bold text-lg">₹8,260</span>
                    <br/><span className="text-slate-500 text-xs">(₹7,000 + 18% GST)</span>
                  </td>
                </tr>
                <tr className="border-b border-white/10 hover:bg-white/5 transition-colors border-l-2 border-l-blue-400/50">
                  <td className="py-4 px-4 font-medium text-blue-300">Applied for GOS Membership</td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-[#FFB6C1] font-bold text-lg">₹6,490</span>
                    <br/><span className="text-slate-500 text-xs">(₹5,500 + 18% GST)</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-white font-bold text-lg">₹8,260</span>
                    <br/><span className="text-slate-500 text-xs">(₹7,000 + 18% GST)</span>
                  </td>
                </tr>
                <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                  <td className="py-4 px-4 font-medium text-white">Non-GOS Members</td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-[#FFB6C1] font-bold text-lg">₹7,670</span>
                    <br/><span className="text-slate-500 text-xs">(₹6,500 + 18% GST)</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-white font-bold text-lg">₹9,440</span>
                    <br/><span className="text-slate-500 text-xs">(₹8,000 + 18% GST)</span>
                  </td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors">
                  <td className="py-4 px-4 font-medium text-white">Spouse / Resident</td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-[#FFB6C1] font-bold text-lg">₹5,900</span>
                    <br/><span className="text-slate-500 text-xs">(₹5,000 + 18% GST)</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-white font-bold text-lg">₹7,670</span>
                    <br/><span className="text-slate-500 text-xs">(₹6,500 + 18% GST)</span>
                  </td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors border-t border-green-500/20 bg-green-500/5">
                  <td className="py-4 px-4 font-medium text-green-400">Senior (70+ years)</td>
                  <td className="py-4 px-4 text-center" colSpan={2}>
                    <span className="text-green-400 font-bold text-lg">Complimentary</span>
                    <br/><span className="text-slate-500 text-xs">(Aadhaar card required for age verification)</span>
                  </td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors border-t border-green-500/20 bg-green-500/5">
                  <td className="py-4 px-4 font-medium text-green-400">National Faculty</td>
                  <td className="py-4 px-4 text-center" colSpan={2}>
                    <span className="text-green-400 font-bold text-lg">Complimentary</span>
                    <br/><span className="text-slate-500 text-xs">(For invited national faculty)</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 p-4 glass-card border-[#FF6F4A]/30">
            <p className="text-sm text-slate-300">
              <span className="text-[#FF6F4A] font-semibold">Note:</span> All fees shown are inclusive of 18% GST.
            </p>
          </div>
        </div>

        {/* UPI Payment QR Code Section */}
        <div className="glass-card p-8 md:p-12 mb-8 neon-glow" data-testid="upi-payment-section">
          <div className="flex items-center gap-3 mb-6">
            <QrCode className="h-7 w-7 text-[#FF6F4A]" />
            <h2 className="text-2xl font-semibold">Step 1: Make Payment via UPI</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-2xl shadow-lg" data-testid="qr-code-container">
                <img
                  src="https://customer-assets.emergentagent.com/job_agos2026/artifacts/vlu1x2jf_WhatsApp%20Image%202026-04-18%20at%205.57.41%20PM.jpeg"
                  alt="UPI QR Code - ALL GUJARAT OPHTHALMOLOG"
                  className="w-64 h-64 object-contain"
                  data-testid="upi-qr-code"
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Payment Details</h3>
              <div className="space-y-3">
                <div className="glass-card p-4">
                  <p className="text-sm text-slate-400">Merchant Name</p>
                  <p className="text-lg font-semibold text-white" data-testid="merchant-name">ALL GUJARAT OPHTHALMOLOG</p>
                </div>
                <div className="glass-card p-4">
                  <p className="text-sm text-slate-400">UPI ID</p>
                  <p className="text-lg font-semibold text-[#FF6F4A]" data-testid="upi-id">agos347@sbi</p>
                </div>
                <div className="glass-card p-4">
                  <p className="text-sm text-slate-400">Bank</p>
                  <p className="text-lg font-semibold text-white">State Bank of India (SBI)</p>
                </div>
              </div>
              <div className="p-4 glass-card border-[#FF6F4A]/30 mt-4">
                <p className="text-sm text-slate-300">
                  <span className="text-[#FF6F4A] font-semibold">How to pay:</span> Scan the QR code using any UPI app (Google Pay, PhonePe, Paytm, BHIM) or pay directly to UPI ID <span className="text-[#FF6F4A] font-medium">agos347@sbi</span>
                </p>
              </div>
            </div>
          </div>

          {/* Bank Transfer - Alternate Option */}
          <div className="mt-8 pt-8 border-t border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-[#FFB6C1]" />
              Alternate: Bank Transfer (NEFT/RTGS)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass-card p-4">
                <p className="text-sm text-slate-400">Account Name</p>
                <p className="text-base font-semibold text-white">All Gujarat Ophthalmological Society</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-sm text-slate-400">Bank</p>
                <p className="text-base font-semibold text-white">State Bank of India</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-sm text-slate-400">Account Number</p>
                <p className="text-lg font-semibold text-[#FF6F4A] font-mono">37579392347</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-sm text-slate-400">IFSC Code</p>
                <p className="text-lg font-semibold text-[#FF6F4A] font-mono">SBIN0008053</p>
              </div>
              <div className="glass-card p-4 md:col-span-2">
                <p className="text-sm text-slate-400">Branch</p>
                <p className="text-base font-semibold text-white">Pragatinagar</p>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="glass-card p-8 md:p-12" data-testid="registration-form">
          <div className="flex items-center gap-3 mb-6">
            <IndianRupee className="h-7 w-7 text-[#FFB6C1]" />
            <h2 className="text-2xl font-semibold">Step 2: Fill Registration Details</h2>
          </div>
          <p className="text-slate-400 text-sm mb-8">After completing payment via UPI, fill in your details below along with the UPI Transaction ID.</p>

          <div className="space-y-6">
            <div data-testid="form-field-name">
              <Label htmlFor="full_name" className="text-white">Full Name *</Label>
              <Input
                id="full_name"
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                className="glass-card border-white/20 text-white mt-2"
                placeholder="Dr. John Doe"
              />
            </div>

            <div data-testid="form-field-delegate-photo">
              <Label className="text-white">Upload Your Photo *</Label>
              <div className="mt-2">
                <label
                  htmlFor="delegate-photo-upload"
                  className="flex items-center justify-center gap-3 glass-card border-2 border-dashed border-white/20 hover:border-[#FF6F4A]/50 p-5 cursor-pointer transition-colors rounded-xl"
                  data-testid="delegate-photo-upload-area"
                >
                  {delegatePhotoPreview ? (
                    <div className="flex items-center gap-4">
                      <img src={delegatePhotoPreview} alt="Delegate photo" className="h-20 w-20 rounded-lg object-cover" />
                      <div>
                        <p className="text-sm text-green-400 font-medium">Photo attached</p>
                        <p className="text-xs text-slate-400">{delegatePhotoFile?.name}</p>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setDelegatePhotoFile(null); setDelegatePhotoPreview(''); }}
                          className="text-xs text-red-400 hover:text-red-300 mt-1"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-300">Click to upload your photo</p>
                      <p className="text-xs text-slate-500">Passport size photo, JPG/PNG up to 5MB</p>
                    </div>
                  )}
                </label>
                <input
                  id="delegate-photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File size must be under 5MB');
                        return;
                      }
                      setDelegatePhotoFile(file);
                      setDelegatePhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div data-testid="form-field-email">
                <Label htmlFor="email" className="text-white">Email {!isNationalFaculty && '*'}</Label>
                <Input
                  id="email"
                  type="email"
                  required={!isNationalFaculty}
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="glass-card border-white/20 text-white mt-2"
                  placeholder={isNationalFaculty ? 'Optional for National Faculty' : 'john@example.com'}
                />
              </div>

              <div data-testid="form-field-phone">
                <Label htmlFor="phone" className="text-white">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="glass-card border-white/20 text-white mt-2"
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div data-testid="form-field-designation">
              <Label htmlFor="designation" className="text-white">Designation *</Label>
              <Input
                id="designation"
                type="text"
                required
                value={formData.designation}
                onChange={(e) => handleChange('designation', e.target.value)}
                className="glass-card border-white/20 text-white mt-2"
                placeholder="Consultant Ophthalmologist"
              />
            </div>

            <div data-testid="form-field-institution">
              <Label htmlFor="institution" className="text-white">Institute / Hospital Name *</Label>
              <Input
                id="institution"
                type="text"
                required
                value={formData.institution}
                onChange={(e) => handleChange('institution', e.target.value)}
                className="glass-card border-white/20 text-white mt-2"
                placeholder="e.g., XYZ Eye Hospital"
              />
            </div>

            <div data-testid="form-field-city">
              <Label htmlFor="city" className="text-white">City *</Label>
              <Input
                id="city"
                type="text"
                required
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="glass-card border-white/20 text-white mt-2"
                placeholder="e.g., Surat, Ahmedabad, Mumbai"
              />
            </div>

            <div data-testid="form-field-registration-type">
              <Label htmlFor="registration_type" className="text-white">Registration Type *</Label>
              <Select
                value={formData.registration_type}
                onValueChange={(value) => handleChange('registration_type', value)}
              >
                <SelectTrigger className="glass-card border-white/20 text-white mt-2">
                  <SelectValue placeholder="Select registration type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C0A4D] border-white/20">
                  {registrationTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      {type.label} - {type.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isGOSMember && (
              <div data-testid="form-field-gos-membership" className="slide-up-animation">
                <Label htmlFor="gos_membership_number" className="text-white">GOS Membership Number *</Label>
                <Input
                  id="gos_membership_number"
                  type="text"
                  required={isGOSMember}
                  value={formData.gos_membership_number}
                  onChange={(e) => handleChange('gos_membership_number', e.target.value)}
                  className="glass-card border-white/20 text-white mt-2"
                  placeholder="Enter your GOS Membership Number"
                />
                <p className="text-xs text-slate-400 mt-2">Don&apos;t know your number? <a href="/agos" className="text-[#FF6F4A] hover:underline">Check here</a></p>
              </div>
            )}

            {isAppliedGOS && (
              <div className="glass-card p-4 border-blue-400/30 bg-blue-500/5 slide-up-animation">
                <p className="text-sm text-blue-300">You have selected &quot;Applied for GOS Membership&quot;. Please ensure you have applied for membership. Visit the <a href="/agos" className="text-[#FF6F4A] hover:underline font-semibold">AGOS page</a> for membership details.</p>
              </div>
            )}

            {isSenior70 && (
              <div data-testid="form-field-aadhaar" className="glass-card p-6 border-2 border-green-500/40 bg-green-500/5 slide-up-animation">
                <Label className="text-white text-lg font-semibold mb-2 block">Aadhaar Card Upload *</Label>
                <p className="text-sm text-slate-400 mb-4">Upload a copy of your Aadhaar card for age verification (Born on or before 23rd October 1956)</p>
                <label
                  htmlFor="aadhaar-upload"
                  className="flex items-center justify-center gap-3 glass-card border-2 border-dashed border-white/20 hover:border-green-500/50 p-6 cursor-pointer transition-colors rounded-xl"
                >
                  {aadhaarPreview ? (
                    <div className="flex items-center gap-4">
                      <img src={aadhaarPreview} alt="Aadhaar card" className="h-20 rounded-lg object-cover" />
                      <div>
                        <p className="text-sm text-green-400 font-medium">Aadhaar card attached</p>
                        <p className="text-xs text-slate-400">{aadhaarFile?.name}</p>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setAadhaarFile(null); setAadhaarPreview(''); }}
                          className="text-xs text-red-400 hover:text-red-300 mt-1"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="h-8 w-8 text-green-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-300">Click to upload Aadhaar card</p>
                      <p className="text-xs text-slate-500">JPG, PNG up to 5MB</p>
                    </div>
                  )}
                </label>
                <input
                  id="aadhaar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) { toast.error('File size must be under 5MB'); return; }
                      setAadhaarFile(file);
                      setAadhaarPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </div>
            )}

            {isNationalFaculty && (
              <div data-testid="form-field-national-faculty" className="glass-card p-4 border-2 border-green-500/40 bg-green-500/5 slide-up-animation">
                <p className="text-sm text-green-300">You have selected <strong>National Faculty - Complimentary</strong>. No payment is required. Your complimentary registration will be verified by the organising committee.</p>
              </div>
            )}

            {!isComplimentary && (
            <div data-testid="form-field-upi-transaction" className="glass-card p-6 border-2 border-[#FF6F4A]/40 bg-[#FF6F4A]/5">
              <Label className="text-white text-lg font-semibold mb-2 block">Payment Proof *</Label>
              <p className="text-sm text-slate-400 mb-4">Enter your UPI Transaction ID <strong className="text-white">or</strong> upload a payment screenshot (or both)</p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="upi_transaction_id" className="text-white text-sm">UPI Transaction ID / Reference Number</Label>
                  <Input
                    id="upi_transaction_id"
                    type="text"
                    value={formData.upi_transaction_id}
                    onChange={(e) => handleChange('upi_transaction_id', e.target.value)}
                    className="glass-card border-white/20 text-white mt-1"
                    placeholder="e.g., 425619283746 or UPI Ref No."
                  />
                </div>

                <div className="border-t border-white/10 pt-4">
                  <Label className="text-white text-sm mb-2 block">
                    <Upload className="inline h-4 w-4 mr-1" />
                    Upload Payment Screenshot
                  </Label>
                  <div className="mt-2">
                    <label
                      htmlFor="screenshot-upload"
                      className="flex items-center justify-center gap-3 glass-card border-2 border-dashed border-white/20 hover:border-[#FF6F4A]/50 p-6 cursor-pointer transition-colors rounded-xl"
                      data-testid="screenshot-upload-area"
                    >
                      {screenshotPreview ? (
                        <div className="flex items-center gap-4">
                          <img src={screenshotPreview} alt="Payment screenshot" className="h-20 rounded-lg object-cover" />
                          <div>
                            <p className="text-sm text-green-400 font-medium">Screenshot attached</p>
                            <p className="text-xs text-slate-400">{screenshotFile?.name}</p>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); setScreenshotFile(null); setScreenshotPreview(''); }}
                              className="text-xs text-red-400 hover:text-red-300 mt-1"
                              data-testid="remove-screenshot-button"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <ImageIcon className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-300">Click to upload screenshot</p>
                          <p className="text-xs text-slate-500">JPG, PNG up to 5MB</p>
                        </div>
                      )}
                    </label>
                    <input
                      id="screenshot-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('File size must be under 5MB');
                            return;
                          }
                          setScreenshotFile(file);
                          setScreenshotPreview(URL.createObjectURL(file));
                        }
                      }}
                      data-testid="screenshot-file-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            )}

            <Button
              type="submit"
              disabled={loading || uploadingScreenshot}
              className="w-full bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold py-6 text-lg neon-glow"
              data-testid="submit-registration-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {uploadingScreenshot ? 'Uploading Screenshot...' : 'Submitting Registration...'}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Submit Registration
                </>
              )}
            </Button>
          </div>
        </form>

        <div className="mt-8 text-center text-sm text-slate-400">
          <p>For any payment or registration queries, contact: <a href="mailto:sgaosurat2025@gmail.com" className="text-[#FF6F4A] hover:underline">sgaosurat2025@gmail.com</a></p>
          <p className="mt-2">You will receive a confirmation after payment verification</p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
